import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../services/api";
import { useWebAuth } from "../context/WebAuthContext";
import { formatKg, weightLine } from "../utils/weight";

const statusColor = {
  customer_submitted: "bg-yellow-100 text-yellow-800",
  expert_viewed: "bg-blue-100 text-blue-700",
  registered: "bg-emerald-100 text-emerald-700",
  pending: "bg-yellow-100 text-yellow-800",
  reviewing: "bg-blue-100 text-blue-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700"
};

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fa-IR');
}

const statusLabel = {
  customer_submitted: "ثبت شده توسط مشتری",
  expert_viewed: "بازدید توسط کارشناس",
  rejected: "رد شده",
  registered: "ثبت شده (نهایی)",
};


export default function PreinvoiceDetailPage() {

  const { id } = useParams();
  const { token } = useWebAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {

    if (!token) return;

    setLoading(true);

    api.webPreinvoiceDetail(token, id)
      .then(res => {

        const result =
          res?.data?.data ||   // حالت استاندارد API
          res?.data ||         // حالت ساده
          null;

        setData(result);

      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));

  }, [id, token]);


  const cancelRequest = async () => {
    if (!window.confirm("آیا مطمئن هستید؟")) return;

    try {
      await api.cancelPreinvoice(token, id);
      window.location.reload();
    } catch {
      alert("خطا در لغو درخواست");
    }
  };

  const downloadPdf = () => {
    window.open(api.downloadPreinvoicePdf(token, id));
  };

  if (loading)
    return (
      <div className="text-center py-16">
        در حال دریافت اطلاعات...
      </div>
    );

  if (!data)
    return (
      <div className="text-center py-16">
        پیش‌فاکتور پیدا نشد
      </div>
    );

  const header = data;
  const items = data.items || [];
  const weightedItems = items
    .map((item) => ({ ...item, title: item.title || item.product_title, weight: weightLine(item) }))
    .filter((item) => item.weight.hasWeight);
  const totalWeightKg = weightedItems.reduce((sum, item) => sum + item.weight.totalKg, 0);

  return (
    <main className="bg-gray-50 min-h-[70vh]" dir="rtl">

      <div className="pp-container py-10">

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl font-extrabold">
            پیش‌فاکتور #{header.id}
          </h1>

          <Link
            to="/account"
            className="text-sm text-blue-600 hover:underline"
          >
            بازگشت به پنل
          </Link>
        </div>

        <div className="bg-white border rounded-2xl p-6 mb-6">

          <div className="flex justify-between items-center mb-4">

            <span className={`px-3 py-1 text-xs rounded-full font-bold ${statusColor[header.status]}`}>
              {statusLabel[header.status] || header.status}
            </span>

            <button
              onClick={downloadPdf}
              className="text-sm px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              دانلود PDF
            </button>

          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">

            <div>
              <div className="text-gray-500">نام مشتری</div>
              <div className="font-bold">{header.customer_name}</div>
            </div>

            <div>
              <div className="text-gray-500">تلفن</div>
              <div className="font-bold">{header.customer_phone}</div>
            </div>

            <div>
              <div className="text-gray-500">تاریخ</div>
              <div className="font-bold">
                {formatDate(header.created_at || header.updated_at)}
              </div>
            </div>

            <div>
              <div className="text-gray-500">نوع پیش‌فاکتور</div>
              <div className="font-bold">{header.pay_mode === "official" ? "رسمی" : "غیر رسمی"}</div>
            </div>

            <div>
              <div className="text-gray-500">مبلغ کل</div>
              <div className="font-bold text-emerald-700">
                {Number(header.total_amount || 0).toLocaleString("fa-IR")} ریال
              </div>
            </div>

          </div>

        </div>

        {header.expert_message && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="font-bold mb-1 text-blue-800">
              پیام کارشناس فروش
            </div>
            <div className="text-sm text-blue-900">
              {header.expert_message}
            </div>
          </div>
        )}

        {weightedItems.length > 0 && (
          <div className="mb-6 overflow-hidden rounded-2xl border bg-white">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div className="font-bold">محاسبه وزن پیش‌فاکتور</div>
              <div className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-[#005bbb]">
                مجموع: {formatKg(totalWeightKg)}
              </div>
            </div>
            <div className="overflow-auto">
              <table className="w-full min-w-[680px] text-center text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-center">ردیف</th>
                    <th className="px-4 py-3 text-center">محصول</th>
                    <th className="px-4 py-3 text-center">وزن هر شاخه</th>
                    <th className="px-4 py-3 text-center">تعداد</th>
                    <th className="px-4 py-3 text-center">مجموع وزن</th>
                  </tr>
                </thead>
                <tbody>
                  {weightedItems.map((item, index) => (
                    <tr key={item.id} className="border-t transition hover:bg-slate-100/70">
                      <td className="px-4 py-3 text-gray-500">{(index + 1).toLocaleString('fa-IR')}</td>
                      <td className="px-4 py-3 font-semibold">{item.title || '-'}</td>
                      <td className="px-4 py-3">
                        {item.weight.parsed ? formatKg(item.weight.parsed.kg) : item.weight.weight_value}
                      </td>
                      <td className="px-4 py-3">{Number(item.weight.quantity || 0).toLocaleString('fa-IR')}</td>
                      <td className="px-4 py-3 font-bold text-[#005bbb]">
                        {item.weight.parsed ? formatKg(item.weight.totalKg) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border overflow-hidden mb-6">

          <div className="px-5 py-4 border-b font-bold">
            محصولات
          </div>

          <table className="w-full text-center text-sm">

            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-4 py-3 text-center">ردیف</th>
                <th className="px-4 py-3 text-center">محصول</th>
                <th className="px-4 py-3 text-center">برند</th>
                <th className="px-4 py-3 text-center">تعداد</th>
                <th className="px-4 py-3 text-center">واحد سنجش</th>
                <th className="px-4 py-3 text-center">قیمت</th>
                <th className="px-4 py-3 text-center">جمع</th>
              </tr>
            </thead>

            <tbody>

              {items.map((it, index) => (

                <tr key={it.id} className="border-t">

  {/* نام محصول */}
  <td className="px-4 py-3 text-gray-500">
    {(index + 1).toLocaleString("fa-IR")}
  </td>

  {/* نام محصول */}
  <td className="px-4 py-3 font-semibold">
    {it.title || "-"}
  </td>

  {/* برند */}
  <td className="px-4 py-3">
    {it.brand || it.brand_name || "-"}
  </td>

  {/* تعداد */}
  <td className="px-4 py-3">
    {it.quantity || it.qty || "-"}
  </td>

  {/* وزن */}
  <td className="px-4 py-3">
    {it.unit || "-"}
  </td>

  {/* قیمت واحد */}
  <td className="px-4 py-3">
    {Number(it.unit_price || it.price || 0).toLocaleString("fa-IR")}
  </td>

  {/* جمع کل */}
  <td className="px-4 py-3 font-bold text-emerald-700">
    {Number(
      (it.unit_price || it.price || 0) *
      (it.quantity || it.qty || 0)
    ).toLocaleString("fa-IR")}
  </td>

</tr>


              ))}

            </tbody>

          </table>

          <div className="p-5 text-left">
            <div className="inline-flex flex-col gap-2 text-sm text-gray-600">
              <div>جمع کالاها: <b>{Number(header.subtotal_amount || 0).toLocaleString("fa-IR")}</b> ریال</div>
              {Number(header.vat_amount || 0) > 0 && (
                <div>ارزش افزوده ۱۰٪: <b>{Number(header.vat_amount || 0).toLocaleString("fa-IR")}</b> ریال</div>
              )}
              <div className="font-extrabold text-emerald-700 text-base">
                مبلغ کل: {Number(header.total_amount || 0).toLocaleString("fa-IR")} ریال
              </div>
            </div>
          </div>

        </div>

        <div className="bg-white border rounded-2xl p-6 mt-10">

  <div className="font-bold text-lg mb-6">
    وضعیت سفارش
  </div>

  <div className="flex justify-between items-center relative">

    {/* خط پشت مرحله‌ها */}
    <div className="absolute left-0 right-0 top-1/2 transform -translate-y-1/2 h-1 bg-gray-200"></div>

    {/* --------------- مرحله ۱ --------------- */}
    <div className="relative z-10 flex flex-col items-center w-1/3">

      {/* آیکون */}
      <div className={
        header.status === "customer_submitted"
          ? "w-10 h-10 flex items-center justify-center bg-yellow-100 text-yellow-600 rounded-full border-2 border-yellow-400"
          : "w-10 h-10 flex items-center justify-center bg-emerald-100 text-emerald-600 rounded-full border-2 border-emerald-400"
      }>
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9 6h2v5H9V6zm0 6h2v2H9v-2z"/>
        </svg>
      </div>

      <span className={
        header.status === "customer_submitted"
          ? "mt-2 text-sm font-bold text-yellow-700"
          : "mt-2 text-sm text-gray-600"
      }>
        ثبت مشتری
      </span>

    </div>

    {/* --------------- مرحله ۲ --------------- */}
    <div className="relative z-10 flex flex-col items-center w-1/3">

      <div className={
        header.status === "expert_viewed"
          ? "w-10 h-10 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full border-2 border-blue-400"
          : header.status === "registered"
          ? "w-10 h-10 flex items-center justify-center bg-emerald-100 text-emerald-600 rounded-full border-2 border-emerald-400"
          : header.status === "rejected"
          ? "w-10 h-10 flex items-center justify-center bg-red-100 text-red-600 rounded-full border-2 border-red-400"
          : "w-10 h-10 flex items-center justify-center bg-gray-200 text-gray-500 rounded-full border-2 border-gray-300"
      }>
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 4c-4.418 0-8 3.582-8 8a8 8 0 0016 0c0-4.418-3.582-8-8-8zm0 2a6 6 0 016 6c0 .338-.03.67-.09.995A4.977 4.977 0 0010 12a4.977 4.977 0 00-5.91 1.995A6.01 6.01 0 014 10a6 6 0 016-6zm0 4a2 2 0 100 4 2 2 0 000-4z"/>
        </svg>
      </div>

      <span className={
        header.status === "expert_viewed"
          ? "mt-2 text-sm font-bold text-blue-700"
          : header.status === "registered"
          ? "mt-2 text-sm text-gray-600"
          : header.status === "rejected"
          ? "mt-2 text-sm text-red-600"
          : "mt-2 text-sm text-gray-400"
      }>
        بررسی کارشناس
      </span>

    </div>

    {/* --------------- مرحله ۳ --------------- */}
    <div className="relative z-10 flex flex-col items-center w-1/3">

      <div className={
        header.status === "registered"
          ? "w-10 h-10 flex items-center justify-center bg-emerald-100 text-emerald-600 rounded-full border-2 border-emerald-400"
          : header.status === "rejected"
          ? "w-10 h-10 flex items-center justify-center bg-red-100 text-red-600 rounded-full border-2 border-red-400"
          : "w-10 h-10 flex items-center justify-center bg-gray-200 text-gray-500 rounded-full border-2 border-gray-300"
      }>
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M16.707 5.293l-8.414 8.414-4-4L6.293 8.293 8.293 10.293l6.414-6.414 2 2z"/>
        </svg>
      </div>

      <span className={
        header.status === "registered"
          ? "mt-2 text-sm font-bold text-emerald-700"
          : header.status === "rejected"
          ? "mt-2 text-sm text-red-600"
          : "mt-2 text-sm text-gray-400"
      }>
        تایید نهایی
      </span>

    </div>

  </div>
</div>


        {header.status === "pending" && (

          <div className="flex gap-3">

            <button
              onClick={cancelRequest}
              className="px-5 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
            >
              لغو درخواست
            </button>

          </div>

        )}

      </div>

    </main>
  );

}
